import { Button, Stack } from "@mui/material";
import { useMemo } from "react";
import { Form } from "react-final-form";

import { useTranslation } from "@/lib/i18n";
import { useAdminDashboard } from "@/features/admin/hooks";
import { Select, TextField } from "@/features/shared/components";
import {
  useTimeRangeSelectorMutation,
  useTimezoneMutation,
} from "@/features/admin/hooks/mutations";
import { useTimeRangeSelectorQuery, useTimezoneQuery } from "@/features/shared/hooks";
import { UpdateTimeZoneSettingsUpdateBodyTimeZoneTypeEnum } from "@/lib/api";

interface FormValues {
  default_start: number;
  default_end: number;
  time_zone_type: UpdateTimeZoneSettingsUpdateBodyTimeZoneTypeEnum;
  utc_offset: string;
  label_override: string;
}

const TimeSettings = () => {
  const { t } = useTranslation();
  const dashboard = useAdminDashboard();
  const timeRangeSelectorQuery = useTimeRangeSelectorQuery(dashboard.id);
  const timeRangeSelectorMutation = useTimeRangeSelectorMutation(dashboard.id);
  const timezoneQuery = useTimezoneQuery(dashboard.id);
  const timezoneMutation = useTimezoneMutation(dashboard.id);

  const [timesStart, timesEnd] = useMemo(() => {
    const mapTime = (duration: number) => {
      const hours = duration;
      const timePeriod = hours % 24 === 0 ? "days" : "hours";

      let label = t("common.now");
      if (timePeriod === "hours" && hours !== 0) {
        label = t("common.hours", { hours });
      }
      if (timePeriod === "days" && hours !== 0) {
        const days = hours / 24;
        if (days === 1) {
          label = t("common.days_singular", { days });
        } else {
          label = t("common.days", { days });
        }
      }

      return {
        label,
        value: duration,
      };
    };
    const startTimes = timeRangeSelectorQuery.data?.start_options?.map(mapTime) ?? [];
    const endTimes = timeRangeSelectorQuery.data?.end_options?.map(mapTime) ?? [];

    return [startTimes, endTimes];
  }, [timeRangeSelectorQuery.data]);

  const timezoneOptions = [
    {
      label: "Lokale tijd",
      value: "system_time",
    },
    {
      label: "UTC offset",
      value: "utc_offset",
    },
  ];
  const offsetOptions = [
    "-12:00",
    "-11:00",
    "-10:00",
    "-09:30",
    "-09:00",
    "-08:00",
    "-07:00",
    "-06:00",
    "-05:00",
    "-04:00",
    "-03:30",
    "-03:00",
    "-02:00",
    "-01:00",
    "+00:00",
    "+01:00",
    "+02:00",
    "+03:00",
    "+03:30",
    "+04:00",
    "+04:30",
    "+05:00",
    "+05:30",
    "+05:45",
    "+06:00",
    "+06:30",
    "+07:00",
    "+08:00",
    "+08:45",
    "+09:00",
    "+09:30",
    "+10:00",
    "+10:30",
    "+11:00",
    "+12:00",
    "+12:45",
    "+13:00",
    "+14:00",
  ].map((time) => ({ label: time, value: time }));

  async function onSubmit(values: FormValues) {
    timeRangeSelectorMutation.mutate({
      default_start: values.default_start,
      default_end: values.default_end,
    });
    timezoneMutation.mutate({
      time_zone_type: values.time_zone_type,
      utc_offset: values.time_zone_type === "utc_offset" ? values.utc_offset : undefined,
      label_override: values.label_override,
    });
  }

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={{
        default_start: timeRangeSelectorQuery.data?.default_start ?? -4,
        default_end: timeRangeSelectorQuery.data?.default_end ?? 8,
        time_zone_type: timezoneQuery.data?.time_zone_type ?? timezoneOptions[0].value,
        utc_offset: timezoneQuery.data?.utc_offset ?? "+00:00",
        label_override: timezoneQuery.data?.label_override ?? "",
      }}
      render={({ handleSubmit, values, pristine }) => (
        <Stack gap={2}>
          <Stack flexDirection="row" gap={2}>
            <Select
              name="time_zone_type"
              label={t("admin.dashboard_settings.time_settings.time_zone_name")}
              options={timezoneOptions}
            />
            {values.time_zone_type === "utc_offset" && (
              <Select
                name="utc_offset"
                label={t("admin.dashboard_settings.time_settings.utc_offset")}
                options={offsetOptions}
              />
            )}
          </Stack>

          <TextField
            name="label_override"
            label={t("admin.dashboard_settings.time_settings.label_override")}
          />

          <Stack flexDirection="row" gap={2}>
            <Select
              name="default_start"
              label={t("admin.dashboard_settings.time_settings.default_start")}
              options={timesStart}
            />
            <Select
              name="default_end"
              label={t("admin.dashboard_settings.time_settings.default_end")}
              options={timesEnd}
            />
          </Stack>
          <Stack alignItems="flex-end">
            <Button
              disabled={pristine && !timeRangeSelectorQuery.isError}
              onClick={handleSubmit}
              variant="outlined"
            >
              {t("general.save")}
            </Button>
          </Stack>
        </Stack>
      )}
    />
  );
};

export default TimeSettings;
